import type {panelViews} from '../../components/PanelBank/types';
import {makeTrackEvent} from './makeTrackEvent';
import {type NamedWorkspaceType} from './workspacesEvents';

export type PanelCountName = keyof typeof panelViews;
export type PanelCountProperty = `num${Capitalize<PanelCountName>}Panels`;
export type AutoPanelCountProperty =
  `numAuto${Capitalize<PanelCountName>}Panels`;

/**
 * One flat count per panel type, so each count is a fixed warehouse column.
 *  Every count is sent, including zeros, so sums and averages need no missing-value rule.
 */
export type PanelTypeCounts = Record<
  PanelCountProperty | AutoPanelCountProperty,
  number
>;

export type WorkspacePanelInventoryEvent = {
  schemaVersion: number;
  entityName: string;
  projectName: string;
  viewId?: string;
  viewName: string;
  viewType: string;
  workspaceType: NamedWorkspaceType;
  workspaceMode: 'auto' | 'manual' | 'pending';
  numSections: number;
  numAutoSections: number;
  numPanels: number;
  numAutoPanels: number;
  panelKinds: string[];
  autoPanelKinds: string[];
  truncated: boolean;
} & PanelTypeCounts;

type PanelCountDescriptions = {
  [Property in PanelCountProperty]: {
    description: 'number of manual panels (isAuto: false).';
  };
} & {
  [Property in AutoPanelCountProperty]: {
    description: 'number of auto panels (isAuto true or unset).';
  };
};

export const trackWorkspacePanelInventory = makeTrackEvent<
  WorkspacePanelInventoryEvent,
  PanelCountDescriptions & {
    schemaVersion: {
      description: 'version';
      exampleValues: [1];
    };
    entityName: {
      description: 'entity that owns the workspace project';
      exampleValues: ['big-brain-model'];
    };
    projectName: {
      description: 'project the workspace belongs to';
      exampleValues: ['panel-sandbox'];
    };
    viewId: {
      description: 'server ID of the view, the join key to the views table. absent if the view has not been saved yet';
      exampleValues: ['VmlldzoxMjM0NTY3'];
    };
    viewName: {
      description: 'the "name" field in the views table, which encodes the ?nw= workspace ID';
      exampleValues: ['nw-abc123-v', 'workspace'];
    };
    viewType: {
      description: 'the "type" field in the views table';
      exampleValues: ['project-view', 'sweep-view', 'group-view'];
    };
    workspaceType: {
      description: 'whose workspace this is, with the same values as the workspaceType of Named workspaces: a personal workspace, a shared saved view, or the legacy versions of either';
      exampleValues: [
        'personal',
        'shared',
        'legacy personal',
        'legacy shared',
        'unknown',
      ];
    };
    workspaceMode: {
      description: 'whether the workspace auto-generates panels from logged keys. pending means the user has not chosen a mode yet';
      exampleValues: ['auto', 'manual', 'pending'];
    };
    numSections: {
      description: 'number of sections in the panel bank';
    };
    numAutoSections: {
      description: 'number of sections whose isPanelsAuto is true or unset. a section a user creates is saved with isPanelsAuto: false and counts as manual';
    };
    numPanels: {
      description: 'number of panels';
    };
    numAutoPanels: {
      description: 'number of panels whose isAuto is true or unset.';
    };
    panelKinds: {
      description: 'sorted distinct kinds among manual panels (isAuto: false)';
      exampleValues: [
        [
          'barChart:violin',
          'markdown',
          'mediaBrowser:audio',
          'mediaBrowser:video',
          'runHistoryLinePlot:stacked-area',
          'vega2:confusion_matrix/v1',
          'vega2:custom',
        ],
      ];
    };
    autoPanelKinds: {
      description: 'sorted distinct kinds among auto panels (isAuto true or unset), same values as panelKinds';
      exampleValues: [
        ['mediaBrowser:image', 'runHistoryLinePlot:line', 'weave'],
      ];
    };
    truncated: {
      description: 'true if panelKinds or autoPanelKinds was trimmed to fit 512 characters of JSON, the length Segment keeps in a warehouse string column. the num*Panels counts are never trimmed';
      exampleValues: [false, true];
    };
    _description: 'Inventory of the panel types in a workspace, sent after panels are generated on load, at most once per workspace per browser every 24 hours';
    _location: 'multi-run, group, and sweep workspaces';
    _motivation: 'Measure which panel types and visualizations are in use, including ones with no interaction events';
  }
>('Workspace panel inventory');
